/** The part of the browser a backup is handed over through: the share sheet, or a download. */
export interface BackupHandoffPlatform {
  readonly canShare?: (data: ShareData) => boolean;
  readonly share?: (data: ShareData) => Promise<void>;
  /** Starts a download of the file. */
  readonly download: (file: File) => void;
}

export interface BackupFileToHandOver {
  readonly fileName: string;
  readonly text: string;
}

const BACKUP_MEDIA_TYPE = 'application/octet-stream';

/**
 * Hands a backup file to the owner, and answers whether it left: through the phone's share sheet
 * when it can take a file (Files, iCloud, Drive, e-mail…), otherwise as a download. A sheet the
 * owner closes is not a backup made — the day is only noted once the file has gone somewhere.
 */
export const handOverBackup = async (
  file: BackupFileToHandOver,
  platform: BackupHandoffPlatform,
): Promise<boolean> => {
  const handed = new File([file.text], file.fileName, { type: BACKUP_MEDIA_TYPE });
  const data: ShareData = { files: [handed], title: file.fileName };
  if (platform.share !== undefined && platform.canShare?.(data) === true) {
    try {
      await platform.share(data);
      return true;
    } catch {
      return false;
    }
  }
  platform.download(handed);
  return true;
};

/** The browser's own download: a link to the file, clicked, then let go. */
export const browserDownload =
  (documentRef: Document): ((file: File) => void) =>
  (file) => {
    const url = URL.createObjectURL(file);
    const link = documentRef.createElement('a');
    link.href = url;
    link.download = file.name;
    link.rel = 'noopener';
    documentRef.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };
